<template>
  <aside class="ops-sp" role="dialog" aria-label="Practice Ops settings" data-test="ops-settings-panel">
    <header class="ops-sp__head">
      <h2 class="ops-sp__title">Practice Ops settings</h2>
      <button type="button" class="ops-sp__close" title="Close" aria-label="Close" data-test="ops-settings-close" @click="close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </header>

    <!-- The underline tabs of Forms Settings (pfe-tab), ported: a 3px brand underline on the open tab. -->
    <div class="ops-sp__tabs" role="tablist" aria-label="Settings sections">
      <button
        v-for="t in tabs"
        :key="t.id"
        type="button"
        role="tab"
        class="ops-sp__tab"
        :class="{ 'ops-sp__tab--on': tab === t.id }"
        :aria-selected="tab === t.id ? 'true' : 'false'"
        :data-test="`ops-settings-tab-${t.id}`"
        @click="tab = t.id"
      >{{ t.label }}</button>
    </div>

    <div class="ops-sp__body" data-test="ops-settings-body">
      <!-- ── Statuses ─────────────────────────────────────────────────────────────────────── -->
      <section v-if="tab === 'statuses'" class="ops-sp__pane" data-test="ops-settings-pane-statuses">
        <p class="ops-sp__intro">Match your Open Dental appointment statuses to the steps on the board. The colours come from Open Dental and show only in the status dot.</p>
        <p v-if="statusesState === 'loading'" class="ops-sp__note" data-test="ops-settings-statuses-loading">Loading your Open Dental statuses…</p>
        <p v-else-if="statusesState === 'error'" class="ops-sp__note ops-sp__note--bad" data-test="ops-settings-statuses-error">Your Open Dental statuses could not be loaded. The choices below stay as they are; close this panel and try again.</p>
        <div v-for="row in statusRows" :key="row.key" class="ops-sp__status" :data-test="`ops-settings-status-${row.key}`">
          <div class="ops-sp__status-text">
            <div class="ops-sp__status-label">{{ row.label }}</div>
            <div class="ops-sp__status-desc">{{ row.desc }}</div>
          </div>
          <div class="ops-sp__status-pick">
            <span class="ops-sp__dot" :style="{ background: statusColor(row.key) }" title="Open Dental colour"></span>
            <select
              v-model="draft.statuses[row.key]"
              class="ops-sp__select"
              :aria-label="`Open Dental status for ${row.label}`"
              :data-test="`ops-settings-status-select-${row.key}`"
            >
              <option v-if="draft.statuses[row.key] === null" :value="null" disabled>Choose a status</option>
              <option v-for="s in statusOptions(row.key)" :key="s.DefNum" :value="s.DefNum">{{ s.ItemName }}</option>
            </select>
          </div>
        </div>
      </section>

      <!-- ── Operatories: pick, order, rename. No custom rooms. ───────────────────────────── -->
      <section v-else-if="tab === 'ops'" class="ops-sp__pane" data-test="ops-settings-pane-ops">
        <p class="ops-sp__intro">Choose which rooms show on the board, rename them, and drag to set their order. Renaming only changes what Practice Ops shows; Open Dental keeps its own name. A room with a patient in it always shows.</p>
        <p v-if="draft.rooms.length === 0" class="ops-sp__note" data-test="ops-settings-rooms-empty">No operatories yet. They appear here once today's board has been built from Open Dental.</p>
        <draggable v-else v-model="draft.rooms" tag="div" class="ops-sp__rooms" handle=".ops-sp__grip" :animation="150" data-test="ops-settings-rooms">
          <div v-for="(room, index) in draft.rooms" :key="room.opNum" class="ops-sp__room" :data-test="`ops-settings-room-${room.opNum}`">
            <span class="ops-sp__grip" title="Drag to reorder" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>
            </span>
            <input v-model="room.shown" type="checkbox" class="ops-sp__check" :aria-label="`Show ${room.name || room.odName} on the board`" :data-test="`ops-settings-room-show-${room.opNum}`" />
            <input
              v-model="room.name"
              type="text"
              class="ops-sp__input"
              :placeholder="room.odName || 'Room name'"
              :aria-label="`Name for ${room.odName || 'this room'}`"
              :data-test="`ops-settings-room-name-${room.opNum}`"
            />
            <span class="ops-sp__room-src">{{ roomSource(room) }}</span>
            <span class="ops-sp__room-move">
              <button type="button" class="ops-sp__mini" title="Move up" aria-label="Move up" :disabled="index === 0" :data-test="`ops-settings-room-up-${room.opNum}`" @click="moveRoom(index, -1)">▲</button>
              <button type="button" class="ops-sp__mini" title="Move down" aria-label="Move down" :disabled="index === draft.rooms.length - 1" :data-test="`ops-settings-room-down-${room.opNum}`" @click="moveRoom(index, 1)">▼</button>
            </span>
          </div>
        </draggable>
      </section>

      <!-- ── Alerts ──────────────────────────────────────────────────────────────────────── -->
      <section v-else-if="tab === 'alerts'" class="ops-sp__pane" data-test="ops-settings-pane-alerts">
        <div class="ops-sp__card" data-test="ops-settings-waits">
          <h3 class="ops-sp__card-title">Long waits</h3>
          <followups-switch v-model="draft.threshold.on" class="ops-sp__switch" label="Flag patients who wait too long" data-test="ops-settings-threshold-on" />
          <div class="ops-sp__threshold">
            <span>Amber after</span>
            <input
              v-model.number="draft.threshold.minutes"
              type="number"
              min="1"
              :max="maxThreshold"
              inputmode="numeric"
              class="ops-sp__input ops-sp__input--num"
              :disabled="!draft.threshold.on"
              aria-label="Minutes before a wait turns amber"
              data-test="ops-settings-threshold-minutes"
            />
            <span data-test="ops-settings-threshold-red">minutes, red after {{ redAfter }}.</span>
          </div>
          <p class="ops-sp__caption">Applies to the board, the wall display and the report.</p>
        </div>

        <div class="ops-sp__card" data-test="ops-settings-quick">
          <div>
            <h3 class="ops-sp__card-title">Quick alerts</h3>
            <p class="ops-sp__caption">One-tap alerts in the Send alert window. Each one goes to a set team member with set text.</p>
          </div>
          <div v-if="draft.quickAlerts.length" class="ops-sp__cols ops-sp__cols--quick" aria-hidden="true"><span>Team member</span><span>Alert text</span><span></span></div>
          <div v-for="(row, index) in draft.quickAlerts" :key="`q-${index}`" class="ops-sp__row ops-sp__row--quick" data-test="ops-settings-quick-row">
            <select v-model="row.employeeID" class="ops-sp__select" aria-label="Team member" data-test="ops-settings-quick-who">
              <option value="">Choose…</option>
              <option v-for="e in employees" :key="e.EmployeeID" :value="e.EmployeeID">{{ e.Name }}</option>
            </select>
            <input v-model="row.alertText" type="text" class="ops-sp__input" placeholder="e.g. Ready for exam" aria-label="Alert text" data-test="ops-settings-quick-text" />
            <button type="button" class="ops-sp__x" title="Remove" aria-label="Remove quick alert" data-test="ops-settings-quick-remove" @click="draft.quickAlerts.splice(index, 1)">×</button>
          </div>
          <div><button type="button" class="ops-sp__btn" data-test="ops-settings-quick-add" @click="addQuickAlert">+ Add quick alert</button></div>
        </div>

        <div class="ops-sp__card" data-test="ops-settings-team">
          <div>
            <h3 class="ops-sp__card-title">Alert colour and sound per team member</h3>
            <p class="ops-sp__caption">When someone is pinged, the alert card carries their colour on its left edge and their screens play their sound.</p>
          </div>
          <div v-if="employees.length" class="ops-sp__cols ops-sp__cols--team" aria-hidden="true"><span>Team member</span><span>Colour</span><span>Sound</span><span></span></div>
          <div v-for="e in employees" :key="e.EmployeeID" class="ops-sp__row ops-sp__row--team" :data-test="`ops-settings-team-${e.EmployeeID}`">
            <span class="ops-sp__who">{{ e.Name }}</span>
            <span class="ops-sp__colour">
              <label class="ops-sp__swatch" title="Pick a colour">
                <span class="ops-sp__swatch-fill" :style="{ background: draft.team[e.EmployeeID].color || '#dfe4e3' }"></span>
                <input type="color" class="ops-sp__swatch-input" :value="draft.team[e.EmployeeID].color || '#0f7259'" :aria-label="`Colour for ${e.Name}`" :data-test="`ops-settings-color-${e.EmployeeID}`" @input="setTeam(e.EmployeeID, { color: $event.target.value })" />
              </label>
              <button v-if="draft.team[e.EmployeeID].color" type="button" class="ops-sp__mini" title="Use the Open Dental colour" aria-label="Use the Open Dental colour" @click="setTeam(e.EmployeeID, { color: '' })">×</button>
            </span>
            <span class="ops-sp__sound">
              <select v-model="draft.team[e.EmployeeID].sound" class="ops-sp__select" :aria-label="`Sound for ${e.Name}`" :data-test="`ops-settings-sound-${e.EmployeeID}`">
                <option value="">No sound</option>
                <option v-for="s in sounds" :key="s.name" :value="s.name">{{ soundLabel(s.name) }}</option>
              </select>
              <button type="button" class="ops-sp__mini ops-sp__mini--play" title="Play sound" aria-label="Play sound" :disabled="!draft.team[e.EmployeeID].sound" :data-test="`ops-settings-play-${e.EmployeeID}`" @click="playSound(draft.team[e.EmployeeID].sound)">▶</button>
            </span>
          </div>
          <p v-if="!employees.length" class="ops-sp__caption">No active team members yet.</p>
          <p v-else class="ops-sp__caption">A colour you do not set here uses the provider's Open Dental colour.</p>
        </div>

        <div class="ops-sp__card">
          <followups-switch v-model="draft.chatOn" class="ops-sp__switch" label="Show team chat on the Board" data-test="ops-settings-chat" />
        </div>

        <div class="ops-sp__info" data-test="ops-settings-icons-note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
          <span>
            Which alert icons show on a card is set in Schedule View settings. It applies to both pages.
            <a v-if="canOpenScheduleView" href="#" class="ops-sp__link" data-test="ops-settings-open-schedule" @click.prevent="openScheduleView">Open Schedule View</a>
          </span>
        </div>
      </section>

      <!-- ── Check-out ───────────────────────────────────────────────────────────────────── -->
      <section v-else class="ops-sp__pane" data-test="ops-settings-pane-checkout">
        <div class="ops-sp__card" data-test="ops-settings-notes">
          <div class="ops-sp__card-head">
            <div>
              <h3 class="ops-sp__card-title">Quick notes</h3>
              <p class="ops-sp__caption">Shown as chips in the check-out panel, grouped by category. Tapping a chip adds its auto note to the visit notes.</p>
            </div>
            <button type="button" class="ops-sp__btn" data-test="ops-settings-cat-add" @click="addCategory">+ Add category</button>
          </div>
          <div v-for="(cat, ci) in draft.categories" :key="`c-${ci}`" class="ops-sp__cat" data-test="ops-settings-cat">
            <div class="ops-sp__cat-head">
              <input v-model="cat.title" type="text" class="ops-sp__input" placeholder="Category name, like Hygiene" aria-label="Category name" data-test="ops-settings-cat-title" />
              <button type="button" class="ops-sp__x" title="Delete category" aria-label="Delete category" data-test="ops-settings-cat-remove" @click="draft.categories.splice(ci, 1)">×</button>
            </div>
            <div class="ops-sp__cat-items">
              <div v-for="(item, ii) in cat.items" :key="`i-${ci}-${ii}`" class="ops-sp__item" data-test="ops-settings-item">
                <input v-model="item.name" type="text" class="ops-sp__input" placeholder="Chip label, like Recall 3 months" aria-label="Chip label" data-test="ops-settings-item-name" />
                <button type="button" class="ops-sp__x" title="Delete quick note" aria-label="Delete quick note" data-test="ops-settings-item-remove" @click="cat.items.splice(ii, 1)">×</button>
                <textarea v-model="item.autoNotes" rows="2" class="ops-sp__area" placeholder="Auto note added to the visit notes (optional)" aria-label="Auto note" data-test="ops-settings-item-auto"></textarea>
              </div>
              <div><button type="button" class="ops-sp__btn ops-sp__btn--ghost" data-test="ops-settings-item-add" @click="cat.items.push({ name: '', autoNotes: '' })">+ Add quick note</button></div>
            </div>
          </div>
          <p v-if="!draft.categories.length" class="ops-sp__caption" data-test="ops-settings-notes-empty">No quick notes yet. Add a category to get started.</p>
        </div>

        <div class="ops-sp__card" data-test="ops-settings-postop">
          <h3 class="ops-sp__card-title">Post-op email</h3>
          <div class="ops-sp__line">
            <label class="ops-sp__line-label" for="ops-sp-send-time">Send time</label>
            <select id="ops-sp-send-time" v-model="draft.sendEmailTime" class="ops-sp__select ops-sp__select--wide" data-test="ops-settings-send-time">
              <option value="">Not set: no post-op emails</option>
              <option v-for="t in sendTimes" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <p class="ops-sp__caption">The day's follow-up call list is emailed to each provider once, at this hour, on the day of the visit.</p>
        </div>

        <div class="ops-sp__card" data-test="ops-settings-emails">
          <h3 class="ops-sp__card-title">Provider email addresses</h3>
          <div v-for="e in emailRows" :key="e.EmployeeID" class="ops-sp__line" :data-test="`ops-settings-email-row-${e.EmployeeID}`">
            <label class="ops-sp__line-label" :for="`ops-sp-email-${e.EmployeeID}`">{{ e.Name }}</label>
            <input :id="`ops-sp-email-${e.EmployeeID}`" v-model="draft.team[e.EmployeeID].email" type="email" class="ops-sp__input ops-sp__input--email" placeholder="name@practice.com" :data-test="`ops-settings-email-${e.EmployeeID}`" />
          </div>
          <p v-if="!emailRows.length" class="ops-sp__caption">No active team members yet.</p>
        </div>
      </section>
    </div>

    <!-- Pinned: Cancel / Save. The validation message sits on the left. -->
    <footer class="ops-sp__foot" data-test="ops-settings-foot">
      <span class="ops-sp__foot-text" :class="{ 'ops-sp__foot-text--bad': !!errorText }" data-test="ops-settings-foot-text" role="status">{{ errorText || (dirty ? 'Unsaved changes' : '') }}</span>
      <button type="button" class="ops-sp__btn ops-sp__btn--ghost" data-test="ops-settings-cancel" :disabled="saving" @click="close">Cancel</button>
      <button type="button" class="ops-sp__btn ops-sp__btn--primary" data-test="ops-settings-save" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
    </footer>
  </aside>
</template>

<script>
/**
 * Practice Ops settings — the slide-in panel (design "Practice Ops settings", plan "Design review").
 *
 * Opened from the page header's Settings button through `$showPanel` (right, 640 wide). The chrome is Task settings':
 * a 1px border, a header with a close, a scrolling body and Cancel / Save pinned at the bottom. The X and Cancel close WITHOUT
 * saving (emit 'closePanel' — vue2-slideout-panel binds only that event); the page turns the backdrop and Esc off, so a stray
 * click cannot throw a tab of edits away.
 *
 * FOUR TABS: Statuses (the Open Dental status for Arrived / Ready / In treatment / Dismissed), Operatories (pick, order and
 * rename the rooms — NO custom rooms), Alerts (long-wait flag, quick alerts, colour and sound per team member, team chat, a
 * pointer to Schedule View for the icons) and Check-out (quick notes, post-op email time, provider emails).
 *
 * WHERE IT SAVES — the SAME place the classic Configure panel does: the board document `practice-pulse/{siteID}`, field `settings`,
 * through the store (practicepulse/savePracticeOpsSettings). Only the keys the office CHANGED are sent, merged, so every key this
 * panel does not show — the four old visual-alert toggles Schedule View now owns, `ops`, anything newer — is neither shown nor
 * touched, and the nightly reset and the exe (which read `settings` as a whole) see everything that was there. The editing /
 * diffing / payload rules are pure and tested: src/utils/practiceOps/settingsModel.js.
 *
 * ROOMS (Nilay 10/30): the order and the hidden rooms go to `settings.opsLayout { order, hidden }` (Practice Ops only), a rename
 * to `settings.opLabels{<OperatoryNum>: name}` (blank or the Open Dental name ⇒ the key is removed). Classic's `allOps` is never
 * pruned or reordered; a hidden room that holds a patient still shows on the board.
 *
 * TEAM CHAT is the existing `settings.showChat` ('TRUE' / 'FALSE'; absent ⇒ ON since 2026-11-03, exactly as the classic page reads it) — no new key.
 */
import draggable from 'vuedraggable';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import { odColorToHex } from '@/utils/scheduleView/provColor';
import { alertSoundList } from '@/utils/practiceOps/alertSounds';
import {
  STATUS_ROWS, SEND_TIME_OPTIONS, MAX_THRESHOLD_MIN, buildDraft, validateDraft, buildChanges,
} from '@/utils/practiceOps/settingsModel';

const TABS = [
  { id: 'statuses', label: 'Statuses' },
  { id: 'ops', label: 'Operatories' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'checkout', label: 'Check-out' },
];

const clone = (v) => JSON.parse(JSON.stringify(v));

export default {
  name: 'OpsSettingsPanel',

  components: { draggable, FollowupsSwitch },

  props: {
    /** The board document's `settings` as it is now (null / undefined before the first save). */
    stored: { type: Object, default: null },
    /** The board document's own `allOps` — the rooms to start from when no settings exist yet. */
    docAllOps: { type: Array, default: () => [] },
    initialTab: { type: String, default: 'statuses' },
  },

  data() {
    const built = this.makeDrafts();
    return {
      tabs: TABS,
      tab: TABS.some((t) => t.id === this.initialTab) ? this.initialTab : 'statuses',
      baseline: built.baseline,
      draft: built.draft,
      statusRows: STATUS_ROWS,
      statusList: [],
      statusesState: 'loading',
      saving: false,
      errorText: '',
      sendTimes: SEND_TIME_OPTIONS,
      maxThreshold: MAX_THRESHOLD_MIN,
    };
  },

  computed: {
    siteID() {
      const customer = this.$store.state.customer || {};
      return customer.siteID;
    },

    /** Active team members, A to Z. */
    employees() {
      const getter = this.$store.getters['customer/activeEmployees'];
      const list = Array.isArray(getter) ? getter : [];
      return list
        .filter((e) => e && typeof e.EmployeeID === 'string' && this.draft.team[e.EmployeeID])
        .slice()
        .sort((a, b) => String(a.Name || '').localeCompare(String(b.Name || '')));
    },

    /** The provider-email rows: providers first (they are who the post-op list goes to), then everyone else. */
    emailRows() {
      const getProvider = this.$store.getters['customer/getProviderFromEmployeeID'];
      const isProvider = (e) => {
        try { return typeof getProvider === 'function' && !!getProvider(e.EmployeeID); } catch (err) { return false; }
      };
      const flagged = this.employees.map((e) => ({ e, p: isProvider(e) }));
      return flagged.filter((x) => x.p).concat(flagged.filter((x) => !x.p)).map((x) => x.e);
    },

    sounds() {
      return alertSoundList(process.env.VUE_APP_NODE_ENV === 'development');
    },

    dirty() {
      return JSON.stringify(this.draft) !== JSON.stringify(this.baseline);
    },

    redAfter() {
      const n = Number(this.draft.threshold.minutes);
      return Number.isFinite(n) && n > 0 ? n * 2 : '2N';
    },

    firstTime() {
      return !this.currentSettings();
    },

    /** Schedule View: link to it only for someone who can open it (the nav's own rule, `can:schedule`). */
    canOpenScheduleView() {
      try { return typeof this.$can === 'function' && !!this.$can('view', 'schedule'); } catch (e) { return false; }
    },
  },

  created() {
    this.loadStatuses();
  },

  methods: {
    makeDrafts() {
      const ctx = { docAllOps: this.docAllOps, employees: this.activeEmployeesNow() };
      return { baseline: buildDraft(this.stored, ctx), draft: buildDraft(this.stored, ctx) };
    },

    activeEmployeesNow() {
      const getter = this.$store && this.$store.getters ? this.$store.getters['customer/activeEmployees'] : null;
      return Array.isArray(getter) ? getter : [];
    },

    /** The settings as they are RIGHT NOW (a snapshot may have landed since the panel opened), else the prop. */
    currentSettings() {
      const state = this.$store && this.$store.state ? this.$store.state.practicepulse : null;
      const live = state && state.practicePulse ? state.practicePulse.settings : null;
      if (live && typeof live === 'object') return live;
      return this.stored && typeof this.stored === 'object' ? this.stored : null;
    },

    async loadStatuses() {
      this.statusesState = 'loading';
      try {
        const res = await this.$store.dispatch('practicepulse/getPracticePulseStatuses', { siteID: this.siteID });
        if (Array.isArray(res) && res.length) {
          this.statusList = res;
          this.statusesState = 'ready';
        } else {
          this.statusesState = 'error';
        }
      } catch (e) {
        this.statusesState = 'error';
      }
    },

    // ── statuses ────────────────────────────────────────────────────────────────────────────
    /** Every status Open Dental has, plus the stored one when it is not in that list (a hidden definition) — never lose the choice. */
    statusOptions(key) {
      const list = this.statusList.slice();
      const stored = (this.currentSettings() || {})[key];
      if (stored && typeof stored === 'object' && stored.DefNum !== undefined && !list.some((s) => Number(s.DefNum) === Number(stored.DefNum))) {
        list.unshift({ DefNum: stored.DefNum, ItemName: stored.ItemName || stored.ItemValue || `Status ${stored.DefNum}` });
      }
      return list;
    },

    statusColor(key) {
      const defNum = this.draft.statuses[key];
      const found = this.statusOptions(key).find((s) => Number(s.DefNum) === Number(defNum));
      return (found && odColorToHex(found.ItemColor)) || '#dfe4e3';
    },

    // ── rooms ───────────────────────────────────────────────────────────────────────────────
    roomSource(room) {
      if (room.custom) return 'Custom room';
      const name = String(room.name || '').trim();
      return name && name !== room.odName ? `Open Dental: ${room.odName}` : 'From Open Dental';
    },

    moveRoom(index, delta) {
      const to = index + delta;
      if (to < 0 || to >= this.draft.rooms.length) return;
      const rooms = this.draft.rooms.slice();
      const [moved] = rooms.splice(index, 1);
      rooms.splice(to, 0, moved);
      this.draft.rooms = rooms;
    },

    // ── alerts ──────────────────────────────────────────────────────────────────────────────
    addQuickAlert() {
      this.draft.quickAlerts.push({ employeeID: '', alertText: '' });
    },

    setTeam(id, patch) {
      this.draft.team[id] = Object.assign({}, this.draft.team[id], patch);
    },

    soundLabel(name) {
      const words = String(name).replace(/-/g, ' ');
      return words.charAt(0).toUpperCase() + words.slice(1);
    },

    playSound(name) {
      const sound = this.sounds.find((s) => s.name === name);
      if (!sound) return;
      try {
        const audio = new Audio(sound.url);
        const p = audio.play();
        if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked */ });
      } catch (e) { /* no audio here */ }
    },

    openScheduleView() {
      this.close();
      if (this.$router && typeof this.$router.push === 'function') {
        const result = this.$router.push({ path: '/app/schedule' });
        if (result && typeof result.catch === 'function') result.catch(() => {});
      }
    },

    // ── check-out ───────────────────────────────────────────────────────────────────────────
    addCategory() {
      this.draft.categories.push({ key: '', title: '', items: [{ name: '', autoNotes: '' }] });
    },

    // ── save / close ────────────────────────────────────────────────────────────────────────
    async save() {
      if (this.saving) return;
      this.errorText = '';
      const problems = validateDraft(this.draft, { firstTime: this.firstTime });
      if (problems.length) {
        this.tab = problems[0].tab;
        this.errorText = problems[0].message;
        return;
      }

      const stored = this.currentSettings();
      const { values, sections } = buildChanges(stored, this.draft, this.baseline, {
        docAllOps: this.docAllOps,
        employees: this.employees,
        statuses: this.statusList,
        sounds: this.sounds,
      });
      if (Object.keys(values).length === 0) {
        this.close();
        return;
      }

      this.saving = true;
      try {
        await this.$store.dispatch('practicepulse/savePracticeOpsSettings', { ppFBID: this.siteID, stored: stored ? clone(stored) : null, values });
        if (this.$toast && typeof this.$toast.success === 'function') this.$toast.success('Practice Ops settings saved');
        this.$emit('closePanel', { saved: true, sections });
      } catch (err) {
        this.errorText = 'The settings could not be saved. Check your connection and try again.';
        if (this.$toast && typeof this.$toast.error === 'function') this.$toast.error('Practice Ops settings could not be saved.');
      } finally {
        this.saving = false;
      }
    },

    close() {
      this.$emit('closePanel', { saved: false });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.ops-sp {
  @include fu-font-base;
  display: flex;
  flex-direction: column;
  // Exactly the slide-out's height (vue2-slideout-panel `.slideout` is fixed, top 0 / bottom 0) with the BODY as the scroller,
  // so the Cancel / Save bar stays pinned at the bottom (as Task settings does).
  height: 100%;
  min-height: 0;
  background: $fu-surface;
}

.ops-sp__head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 16px 0 24px;
}

.ops-sp__title {
  @include fu-font-display;
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: $fu-text;
}

.ops-sp__close { @include fu-icon-btn(36px); }

// The underline tabs of Forms Settings (pfe-tab), ported.
.ops-sp__tabs {
  flex: none;
  display: flex;
  gap: 2px;
  padding: 10px 16px 0 20px;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid $fu-border;
}

.ops-sp__tab {
  @include fu-button-reset;
  @include fu-focus-ring;
  position: relative;
  flex: none;
  height: 42px;
  padding: 0 14px;
  border-bottom: 3px solid transparent;
  font-size: 14.5px;
  font-weight: $fu-fw-bold;
  color: $fu-neutral-500;
  white-space: nowrap;
  &:hover { color: $fu-text; }
}

.ops-sp__tab--on {
  color: $fu-brand-700;
  border-bottom-color: $fu-brand;
}

.ops-sp__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.ops-sp__pane {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 22px 24px 28px;
}

.ops-sp__intro {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: $fu-neutral-700;
}

.ops-sp__note {
  margin: 0;
  font-size: 13px;
  color: $fu-text-muted;
}

.ops-sp__note--bad { color: $fu-danger; }

.ops-sp__card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid $fu-border;
  border-radius: 12px;
}

.ops-sp__card-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  > div:first-child { flex: 1; min-width: 0; }
}

.ops-sp__card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: $fu-text;
}

.ops-sp__caption {
  margin: 2px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: $fu-text-muted;
}

.ops-sp__switch {
  align-items: flex-start;
  ::v-deep .fu-switch__track { margin-top: 1px; }
  ::v-deep .fu-switch__label { font-size: $fu-fs-body-sm; font-weight: $fu-fw-medium; line-height: 1.4; }
}

// ---- controls --------------------------------------------------------------------------------
.ops-sp__input {
  @include fu-input(40px);
  min-width: 0;
}

.ops-sp__input--num { width: 84px; flex: none; text-align: center; padding: 0 8px; }
.ops-sp__input--email { flex: 0 1 280px; width: auto; }

.ops-sp__select {
  @include fu-input(40px);
  min-width: 0;
  padding-right: 30px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23717977' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  cursor: pointer;
}

.ops-sp__select--wide { flex: 0 1 260px; width: auto; }

.ops-sp__area {
  @include fu-input(auto);
  grid-column: 1 / -1;
  padding: 8px 12px;
  resize: vertical;
  background: $fu-neutral-50;
  color: $fu-neutral-700;
  font-size: 13.5px;
}

.ops-sp__check { @include fu-checkbox; }

.ops-sp__btn { @include fu-btn(secondary, sm); flex: none; }
.ops-sp__btn--ghost { @include fu-btn(ghost, sm); }
.ops-sp__btn--primary { @include fu-btn(primary, sm); }

.ops-sp__x {
  @include fu-icon-btn(32px);
  font-size: 20px;
  line-height: 1;
}

.ops-sp__mini {
  @include fu-icon-btn(28px);
  font-size: 11px;
  border: 1px solid $fu-border;
}

.ops-sp__mini--play { font-size: 10px; }

.ops-sp__link {
  margin-left: 6px;
  font-weight: $fu-fw-semibold;
  color: $fu-brand-700;
  &:hover { text-decoration: underline; }
}

// ---- statuses ---------------------------------------------------------------------------------
.ops-sp__status {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 230px);
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  border: 1px solid $fu-border;
  border-radius: 12px;
}

.ops-sp__status-label { font-size: 14.5px; font-weight: 800; }
.ops-sp__status-desc { margin-top: 2px; font-size: 12.5px; line-height: 1.45; color: $fu-text-muted; }
.ops-sp__status-pick { display: flex; align-items: center; gap: 8px; min-width: 0; }
.ops-sp__status-pick .ops-sp__select { flex: 1; width: auto; }
.ops-sp__dot { flex: none; width: 10px; height: 10px; border-radius: 50%; }

// ---- rooms ------------------------------------------------------------------------------------
.ops-sp__rooms {
  border: 1px solid $fu-border;
  border-radius: 12px;
  overflow: hidden;
}

.ops-sp__room {
  display: grid;
  grid-template-columns: 16px 22px minmax(0, 1fr) 140px 64px;
  gap: 10px;
  align-items: center;
  padding: 8px 10px 8px 12px;
  border-bottom: 1px solid $fu-neutral-100;
  background: $fu-surface;
  &:last-child { border-bottom: 0; }
}

.ops-sp__grip { display: flex; color: $fu-neutral-400; cursor: grab; }
.ops-sp__room-src { @include fu-ellipsis; font-size: 12.5px; color: $fu-text-muted; }
.ops-sp__room-move { display: inline-flex; gap: 4px; }

// ---- alerts -----------------------------------------------------------------------------------
.ops-sp__threshold {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 14px;
}

.ops-sp__cols {
  display: grid;
  gap: 8px;
  padding: 0 2px;
  font-size: 11.5px;
  font-weight: $fu-fw-bold;
  color: $fu-text-muted;
}

.ops-sp__row {
  display: grid;
  gap: 8px;
  align-items: center;
}

.ops-sp__cols--quick,
.ops-sp__row--quick { grid-template-columns: 180px minmax(0, 1fr) 32px; }

.ops-sp__cols--team,
.ops-sp__row--team { grid-template-columns: minmax(0, 1fr) 84px 190px; }

.ops-sp__who { @include fu-ellipsis; font-size: 14px; font-weight: $fu-fw-semibold; }

.ops-sp__colour,
.ops-sp__sound { display: flex; align-items: center; gap: 6px; min-width: 0; }
.ops-sp__sound .ops-sp__select { flex: 1; width: auto; }

.ops-sp__swatch {
  position: relative;
  display: grid;
  place-items: center;
  width: 52px;
  height: 40px;
  margin: 0;
  border: 1px solid $fu-border-strong;
  border-radius: 10px;
  background: $fu-surface;
  cursor: pointer;
}

.ops-sp__swatch-fill {
  width: 26px;
  height: 18px;
  border-radius: 5px;
  box-shadow: inset 0 0 0 1px rgba(12, 15, 14, 0.15);
}

.ops-sp__swatch-input {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
}

.ops-sp__info {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid $fu-border;
  border-radius: 12px;
  background: $fu-neutral-50;
  font-size: 13px;
  line-height: 1.5;
  color: $fu-neutral-700;
  svg { flex: none; margin-top: 2px; color: $fu-neutral-500; }
}

// ---- check-out --------------------------------------------------------------------------------
.ops-sp__cat {
  border: 1px solid $fu-border;
  border-radius: 12px;
  overflow: hidden;
}

.ops-sp__cat-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 10px 12px;
  background: $fu-neutral-50;
  border-bottom: 1px solid $fu-border;
  .ops-sp__input { flex: 1; }
}

.ops-sp__cat-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 12px;
}

.ops-sp__item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  gap: 6px 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid $fu-neutral-100;
}

.ops-sp__line {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
}

.ops-sp__line-label { flex: 1; min-width: 0; margin: 0; @include fu-ellipsis; }

// ---- footer -----------------------------------------------------------------------------------
.ops-sp__foot {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid $fu-border;
  background: $fu-surface;
}

.ops-sp__foot-text {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-muted;
}

.ops-sp__foot-text--bad { color: $fu-danger; }

@include fu-phone {
  .ops-sp__head { padding: 14px 12px 0 16px; }
  .ops-sp__pane { padding: 16px 16px 24px; }
  .ops-sp__foot { padding: 12px 16px; }
  .ops-sp__status { grid-template-columns: minmax(0, 1fr); gap: 10px; }
  .ops-sp__room { grid-template-columns: 22px minmax(0, 1fr) 64px; }
  .ops-sp__grip,
  .ops-sp__room-src { display: none; }
  .ops-sp__cols--quick,
  .ops-sp__row--quick { grid-template-columns: minmax(0, 1fr) 32px; }
  .ops-sp__cols--quick { display: none; }
  .ops-sp__row--quick .ops-sp__input { grid-column: 1; }
  .ops-sp__cols--team { display: none; }
  .ops-sp__row--team { grid-template-columns: minmax(0, 1fr); gap: 6px; padding-bottom: 10px; border-bottom: 1px solid $fu-neutral-100; }
  .ops-sp__line { flex-direction: column; align-items: stretch; gap: 6px; }
  .ops-sp__input--email,
  .ops-sp__select--wide { flex: none; width: 100%; }
}
</style>

<style lang="scss">
// The design's lighter backdrop behind THIS panel only (as Task settings does); vue2-slideout-panel draws its own
// rgba(0,0,0,.5) layer. The page opens the panel with cssClass 'ops-sp-slideout'. Browsers without :has() keep the darker one.
.slideout-panel:has(.ops-sp-slideout) .slideout-panel-bg {
  background-color: rgba(12, 15, 14, 0.18);
}
</style>
